import { useNavigate, useParams } from 'react-router-dom'

import { type TabItem } from '@factorialco/f0-react/dist/experimental'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import {
  NODE_TYPES,
  getNavigationForTypeFromFunction,
  getNodeTypeFromParams,
  isNodeType,
  nodeTypeChildren,
} from 'modules/job_catalog/lib/nodesUtils'

export const useNodeTabs = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { rootId, familyId, functionId, roleId, levelId } = useParams()

  const { canSeeCompetencies, canSeeItManagement } = useJobCatalogTreeVisibility()

  const nodeType = getNodeTypeFromParams({ familyId, functionId, roleId, levelId })
  const nodeLabel = isNodeType(nodeType) ? nodeTypeChildren(nodeType) : nodeType

  const params: {
    rootId: string
    familyId: string
    functionId?: string
    roleId?: string
    levelId?: string
  } = {
    rootId: rootId || '',
    familyId: familyId || '',
    ...(functionId && { functionId }),
    ...(roleId && { roleId }),
    ...(levelId && { levelId }),
  }

  const navParams = {
    rootId: params.rootId,
    familyId: params.familyId,
    functionId: params.functionId || '',
    roleId: params.roleId || '',
    levelId: params.levelId || '',
  }

  const nav = getNavigationForTypeFromFunction(navigation, nodeType)

  const tabs: TabItem[] = []

  const isNodeLevel = nodeType === NODE_TYPES.LEVEL

  if (!isNodeLevel) {
    tabs.push({
      id: nav.overview.fullPath(navParams),
      label: nodeLabel,
      onClick: () => navigate(nav.overview.fullPath(navParams)),
    })
  }

  if (canSeeCompetencies) {
    tabs.push({
      id: nav.competencies.fullPath(navParams),
      label: i18n.t('job_catalog.nodes.competency.multiple'),
      onClick: () => navigate(nav.competencies.fullPath(navParams)),
    })
  }

  tabs.push(
    {
      id: nav.workingConditions.fullPath(navParams),
      label: i18n.t('job_catalog.nodes.working_conditions.multiple'),
      onClick: () => navigate(nav.workingConditions.fullPath(navParams)),
    },
    {
      id: nav.salaries.fullPath(navParams),
      label: i18n.t('job_catalog.nodes.salaries.multiple'),
      onClick: () => navigate(nav.salaries.fullPath(navParams)),
    },
    {
      id: nav.people.fullPath(navParams),
      label: i18n.t('job_catalog.nodes.people.one'),
      onClick: () => navigate(nav.people.fullPath(navParams)),
    }
  )

  if (canSeeItManagement) {
    tabs.push({
      id: nav.itManagement.fullPath(navParams),
      label: i18n.t('job_catalog.nodes.it_management.one'),
      onClick: () => navigate(nav.itManagement.fullPath(navParams)),
    })
  }

  return tabs
}
